// === COMMON STYLES FOR SLUITSTRAAT SCREENS ===
// Centralized styles to reduce duplication across screens
// Import this file in screens and use the shared styles
// All theme values are loaded from config.json via CSS variables

import React from 'react';

export const commonStyles: Record<string, React.CSSProperties> = {
  // ==================== PAGE LAYOUT ====================
  page: {
    flex: 1,
    backgroundColor: 'var(--background-color, #f9f6ee)',
    padding: '32px 16px 64px',
  },
  container: {
    maxWidth: '1150px',
    margin: '0 auto',
    padding: '0 16px',
  },
  containerNarrow: {
    maxWidth: '672px',
    margin: '0 auto',
  },
  contentHeader: {
    marginBottom: '32px',
    textAlign: 'center' as const,
  },
  title: {
    fontSize: '36px',
    lineHeight: '40px',
    fontWeight: '600',
    color: 'var(--foreground, #1a1a1a)',
    marginBottom: '16px',
    letterSpacing: '-0.025em',
    fontFamily: 'var(--font-geist-sans)',
  },
  subtitle: {
    fontSize: '18px',
    color: 'var(--muted-foreground, #64748b)',
    margin: '0 auto',
    lineHeight: '28px',
  },

  // ==================== FORM CONTAINERS ====================
  formContainer: {
    backgroundColor: '#fff',
    padding: '40px',
    borderRadius: '12px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05), 0 1px 1px -1px rgba(0,0,0,0.04)',
    marginBottom: '30px',
    border: 'none',
  },
  sectionContainer: {
    backgroundColor: '#fff',
    padding: '32px',
    borderRadius: '12px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05), 0 1px 1px -1px rgba(0,0,0,0.04)',
    marginBottom: '24px',
    border: 'none',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '24px',
    paddingBottom: '16px',
    borderBottom: '2px solid #f3f4f6',
  },
  sectionIconContainer: {
    width: '56px',
    height: '56px',
    borderRadius: '12px',
    backgroundColor: '#f7f9fc',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  sectionIcon: {
    width: '28px',
    height: '28px',
    opacity: 1,
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#1f2937',
    margin: '0',
  },

  // ==================== FORM FIELDS ====================
  fieldContainer: {
    marginBottom: '20px',
    flex: 1,
  },
  fieldLabel: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '8px',
  },
  required: {
    color: '#d32f2f',
  },
  fieldInput: {
    width: '100%',
    padding: '12px 16px',
    fontSize: '16px',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box' as const,
    backgroundColor: '#fff',
    color: '#333',
  },
  fieldInputReadonly: {
    width: '100%',
    padding: '12px 16px',
    fontSize: '16px',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    boxSizing: 'border-box' as const,
    backgroundColor: '#f3f4f6',
    color: '#374151',
    cursor: 'default',
  },
  fieldInputError: {
    border: '2px solid #d32f2f',
    backgroundColor: '#fff5f5',
  },
  fieldSelect: {
    width: '100%',
    padding: '12px 16px',
    fontSize: '16px',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    outline: 'none',
    transition: 'all 0.2s ease',
    boxSizing: 'border-box' as const,
    backgroundColor: '#fff',
    color: '#333',
    cursor: 'pointer',
    appearance: 'none' as const,
    backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%239ca3af\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 14px center',
    backgroundSize: '20px',
    paddingRight: '48px',
  },
  fieldSelectReadonly: {
    width: '100%',
    padding: '12px 16px',
    fontSize: '16px',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    boxSizing: 'border-box' as const,
    backgroundColor: '#f3f4f6',
    color: '#374151',
    cursor: 'default',
  },
  fieldSelectError: {
    border: '2px solid #d32f2f',
    backgroundColor: '#fff5f5',
  },
  fieldHint: {
    fontSize: '12px',
    color: '#d1d5db',
    marginTop: '4px',
    marginBottom: '0',
  },
  errorMessage: {
    fontSize: '13px',
    color: '#d32f2f',
    marginTop: '6px',
    marginBottom: '0',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },

  // ==================== CURRENCY INPUT ====================
  currencyInputContainer: {
    position: 'relative' as const,
    display: 'flex',
    alignItems: 'center',
  },
  currencySymbol: {
    position: 'absolute' as const,
    left: '16px',
    fontSize: '16px',
    color: '#666',
    fontWeight: '500',
    pointerEvents: 'none' as const,
    zIndex: 1,
  },
  currencyInput: {
    width: '100%',
    padding: '12px 16px 12px 36px',
    fontSize: '16px',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box' as const,
    backgroundColor: '#fff',
    color: '#333',
  },

  // ==================== PHONE INPUT ====================
  phoneInputContainer: {
    position: 'relative' as const,
    display: 'flex',
    alignItems: 'center',
  },
  phoneIconContainer: {
    position: 'absolute' as const,
    left: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
    pointerEvents: 'none' as const,
  },
  phoneIcon: {
    width: '18px',
    height: '18px',
    color: '#9ca3af',
  },
  phoneInput: {
    width: '100%',
    padding: '10px 12px 10px 42px',
    fontSize: '16px',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    boxSizing: 'border-box' as const,
    fontFamily: 'inherit',
    backgroundColor: '#fff',
    color: '#333',
    transition: 'all 0.2s ease',
  },
  phoneHint: {
    fontSize: '12px',
    color: '#d1d5db',
    marginTop: '6px',
    marginBottom: '0',
    fontStyle: 'italic' as const,
  },

  // ==================== BUTTONS ====================
  buttonPrimary: {
    padding: '12px 32px',
    backgroundColor: 'var(--primary-color, #3646f5)',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
  },
  buttonSecondary: {
    padding: '12px 32px',
    backgroundColor: 'transparent',
    color: '#4b5563',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
  buttonSecondaryHover: {
    backgroundColor: 'rgba(54, 70, 245, 0.08)',
  },
  buttonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
    pointerEvents: 'none' as const,
  },
  button: {
    marginTop: '20px',
    padding: '12px 24px',
    backgroundColor: 'var(--primary-color, #1976d2)',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  },

  // ==================== NAVIGATION ====================
  navigation: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '16px',
    marginTop: '32px',
  },

  // ==================== ADDRESS INDICATORS ====================
  addressLookupLoading: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px 12px',
    backgroundColor: '#f0f7ff',
    border: '1px solid #b3d9ff',
    borderRadius: '6px',
    marginTop: '4px',
    marginBottom: '0px',
  },
  addressLookupSpinner: {
    width: '16px',
    height: '16px',
    border: '2px solid #b3d9ff',
    borderTop: '2px solid var(--primary-color, #1976d2)',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  addressLookupText: {
    fontSize: '14px',
    color: 'var(--primary-color, #1976d2)',
    fontWeight: '500',
  },
  addressValidatedIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px 12px',
    backgroundColor: '#f0fdf4',
    border: '1px solid #86efac',
    borderRadius: '6px',
    marginTop: '4px',
    marginBottom: '0px',
  },
  addressValidatedIcon: {
    width: '16px',
    height: '16px',
    color: '#16a34a',
  },
  addressValidatedText: {
    fontSize: '14px',
    color: '#16a34a',
    fontWeight: '500',
  },

  // ==================== LOCKED BANNERS ====================
  addressLockedBanner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '14px 16px',
    backgroundColor: '#eff6ff',
    border: '1px solid #bfdbfe',
    borderRadius: '6px',
    marginBottom: '20px',
  },
  addressLockedIcon: {
    width: '20px',
    height: '20px',
    color: 'var(--accent-color, #3b82f6)',
    flexShrink: 0,
    marginTop: '2px',
  },
  addressLockedText: {
    fontSize: '13px',
    color: '#1e40af',
    margin: '4px 0 0 0',
  },

  // ==================== ERROR/LOADING CONTAINERS ====================
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: 'var(--background-color, #f5f5f5)',
  },
  errorContainer: {
    maxWidth: '600px',
    margin: '100px auto',
    padding: '40px',
    backgroundColor: '#fff',
    borderRadius: '12px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05), 0 1px 1px -1px rgba(0,0,0,0.04)',
    textAlign: 'center' as const,
  },
  errorTitle: {
    color: '#d32f2f',
    marginBottom: '16px',
  },

  // ==================== MAIN LAYOUT ====================
  mainLayout: {
    display: 'flex',
    gap: '30px',
    marginBottom: '30px',
    alignItems: 'flex-start',
  },
  formColumn: {
    flex: '1 1 100%',
    minWidth: 0,
    maxWidth: '900px',
    margin: '0 auto',
  },

  // ==================== PRODUCT SELECTION ====================
  validationError: {
    padding: '12px 16px',
    backgroundColor: '#fee2e2',
    border: '1px solid #dc2626',
    borderRadius: '10px',
    color: '#dc2626',
    marginBottom: '24px',
    fontSize: '14px',
    textAlign: 'center' as const,
  },
  productsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '24px',
    marginBottom: '40px',
  },
  cardWrapper: {
    position: 'relative' as const,
  },
  recommendedBadge: {
    position: 'absolute' as const,
    top: '-12px',
    left: '24px',
    zIndex: 10,
  },
  recommendedBadgeContent: {
    backgroundColor: '#ec4899',
    color: 'white',
    fontSize: '12px',
    fontWeight: '500',
    padding: '4px 12px',
    borderRadius: '9999px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  productCard: {
    position: 'relative' as const,
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05), 0 1px 1px -1px rgba(0,0,0,0.04)',
    cursor: 'pointer',
    transition: 'all 0.2s',
    border: '1px solid transparent',
    height: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
  },
  productCardHovered: {
    boxShadow: '0 4px 6px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)',
  },
  productCardSelected: {
    border: '1px solid var(--accent-color, #8acdff)',
    backgroundColor: '#eef2ff',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
    transform: 'none',
  },
  selectedBadge: {
    position: 'absolute' as const,
    top: '16px',
    right: '16px',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: '#ec4899',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cardHeader: {
    padding: '24px 32px 0 24px',
    flex: 1,
  },
  iconContainer: {
    width: '56px',
    height: '56px',
    borderRadius: '12px',
    backgroundColor: '#f7f9fc',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '16px',
    transition: 'all 0.2s',
  },
  iconContainerSelected: {
    backgroundColor: 'var(--accent-color, #8acdff)',
  },
  productName: {
    fontSize: '18px',
    fontWeight: '600',
    color: 'var(--foreground, #1a1a1a)',
    marginBottom: '8px',
  },
  productDescription: {
    fontSize: '14px',
    color: 'var(--muted-foreground, #64748b)',
    marginBottom: '12px',
    lineHeight: '1.5',
  },
  cardContent: {
    padding: '24px 32px 28px 24px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '20px',
  },
  productFeatures: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px',
  },
  featureItem: {
    display: 'flex',
    alignItems: 'flex-start',
    fontSize: '14px',
  },
  featureCheck: {
    width: '16px',
    height: '16px',
    marginRight: '12px',
    flexShrink: 0,
    marginTop: '2px',
  },
  featureText: {
    color: '#6b7280',
  },
  bottomSection: {
    borderTop: '1px solid #e5e7eb',
    paddingTop: '32px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '20px',
  },
  selectionSummary: {
    fontSize: '14px',
    fontWeight: '400',
    color: '#4b5563',
    textAlign: 'center' as const,
  },
  navigationSingle: {
    display: 'flex',
    justifyContent: 'center',
    gap: '16px',
  },

  // ==================== SUMMARY PAGE ====================
  successBadge: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, var(--accent-color, #8ACDFF) 0%, var(--primary-color, #1976d2) 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 24px',
    boxShadow: '0 8px 24px rgba(138, 205, 255, 0.3)',
  },
  successIcon: {
    width: '48px',
    height: '48px',
    color: '#fff',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 400px',
    gap: '24px',
    alignItems: 'start',
  },
  leftColumn: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '24px',
  },
  rightColumn: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '24px',
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px',
    marginBottom: '20px',
  },
  infoItem: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
  },
  infoLabel: {
    fontSize: '15px',
    fontWeight: '600',
    color: 'var(--foreground, #1f2937)',
  },
  infoValue: {
    fontSize: '14px',
    color: '#6b7280',
    fontWeight: '400',
  },
  addressSection: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
  },
  productsList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px',
  },
  summaryProductCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '16px',
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
  },
  productIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    backgroundColor: 'var(--accent-color, #8acdff)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  productInfo: {
    flex: 1,
  },
  productCoverage: {
    fontSize: '13px',
    color: '#6b7280',
  },
  productPremium: {
    textAlign: 'right' as const,
  },
  premiumAmount: {
    fontSize: '18px',
    fontWeight: '700',
    color: 'var(--foreground, #1f2937)',
  },
  premiumPeriod: {
    fontSize: '12px',
    color: '#6b7280',
  },
  actionsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
  },
  actionIcon: {
    width: '24px',
    height: '24px',
    color: '#6b7280',
    flexShrink: 0,
  },
  actionContent: {
    flex: 1,
  },
  actionTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: 'var(--foreground, #1f2937)',
    marginBottom: '2px',
  },
  actionSubtitle: {
    fontSize: '12px',
    color: '#6b7280',
  },
  premiumHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '16px',
  },
  premiumIcon: {
    width: '20px',
    height: '20px',
    color: '#6b7280',
  },
  premiumTitle: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#6b7280',
  },
  premiumAmountContainer: {
    textAlign: 'center' as const,
  },
  premiumAmountLarge: {
    fontSize: '26px',
    fontWeight: '700',
    color: 'var(--foreground, #1f2937)',
    lineHeight: '1.1',
    marginBottom: '4px',
  },
  premiumPeriodText: {
    fontSize: '14px',
    color: '#6b7280',
    fontWeight: '500',
  },
  divider: {
    height: '1px',
    backgroundColor: '#e5e7eb',
    marginTop: '12px',
    marginBottom: '12px',
  },
  yearlyPremium: {
    textAlign: 'center' as const,
  },
  yearlyAmount: {
    fontSize: '20px',
    fontWeight: '700',
    color: 'var(--foreground, #1f2937)',
    marginBottom: '4px',
  },
  yearlyPeriod: {
    fontSize: '16px',
    color: '#6b7280',
    fontWeight: '500',
  },
  detailsList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px',
  },
  detailItem: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '6px',
  },
  detailLabel: {
    fontSize: '15px',
    fontWeight: '600',
    color: 'var(--foreground, #1f2937)',
  },
  detailValue: {
    fontSize: '14px',
    color: '#6b7280',
    fontWeight: '400',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  clockIcon: {
    width: '16px',
    height: '16px',
    color: '#6b7280',
  },
  debugActions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center',
    flexWrap: 'wrap' as const,
    marginTop: '40px',
    padding: '20px',
    backgroundColor: '#fff3cd',
    borderRadius: '8px',
    border: '1px dashed #ffc107',
  },
  debugLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#856404',
    margin: '0 0 8px 0',
    width: '100%',
    textAlign: 'center' as const,
  },
  debugButton: {
    padding: '8px 16px',
    backgroundColor: '#fff',
    color: '#856404',
    border: '1px solid #ffc107',
    borderRadius: '4px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  convertCard: {
    backgroundColor: 'var(--primary-color, #1976d2)',
    padding: '32px',
    borderRadius: '16px',
    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
    border: 'none',
    textAlign: 'center' as const,
  },
  convertTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#fff',
    margin: '0 0 12px 0',
    letterSpacing: '-0.02em',
  },
  convertSubtitle: {
    fontSize: '15px',
    color: 'rgba(255, 255, 255, 0.9)',
    margin: '0 0 24px 0',
    lineHeight: '1.5',
  },
  convertButtonIcon: {
    width: '20px',
    height: '20px',
  },
  convertFooter: {
    fontSize: '13px',
    color: 'rgba(255, 255, 255, 0.8)',
    fontWeight: '400',
  },
};

// Export individual style categories for more granular imports if needed
export const fieldStyles = {
  fieldContainer: commonStyles.fieldContainer,
  fieldLabel: commonStyles.fieldLabel,
  required: commonStyles.required,
  fieldInput: commonStyles.fieldInput,
  fieldInputReadonly: commonStyles.fieldInputReadonly,
  fieldInputError: commonStyles.fieldInputError,
  fieldSelect: commonStyles.fieldSelect,
  fieldSelectReadonly: commonStyles.fieldSelectReadonly,
  fieldSelectError: commonStyles.fieldSelectError,
  fieldHint: commonStyles.fieldHint,
  errorMessage: commonStyles.errorMessage,
};

export const buttonStyles = {
  buttonPrimary: commonStyles.buttonPrimary,
  buttonSecondary: commonStyles.buttonSecondary,
  buttonSecondaryHover: commonStyles.buttonSecondaryHover,
  buttonDisabled: commonStyles.buttonDisabled,
  button: commonStyles.button,
};

export const layoutStyles = {
  page: commonStyles.page,
  container: commonStyles.container,
  containerNarrow: commonStyles.containerNarrow,
  contentHeader: commonStyles.contentHeader,
  title: commonStyles.title,
  subtitle: commonStyles.subtitle,
  mainLayout: commonStyles.mainLayout,
  formColumn: commonStyles.formColumn,
};

export const sectionStyles = {
  formContainer: commonStyles.formContainer,
  sectionContainer: commonStyles.sectionContainer,
  sectionHeader: commonStyles.sectionHeader,
  sectionIconContainer: commonStyles.sectionIconContainer,
  sectionIcon: commonStyles.sectionIcon,
  sectionTitle: commonStyles.sectionTitle,
};
